import { type FC, type JSX, type MouseEvent as ReactMouseEvent, type SyntheticEvent, useCallback } from 'react';

import { useListingAppContext } from '@/microfrontends/listing/shared/context/ListingAppContext';
import { useCardOfferDataContext } from '@/microfrontends/listing/shared/context/selectors';
import { useCAForCard } from '@/microfrontends/listing/shared/hooks/card/useCAForCard';

import { useRequestAuthentication } from '@/packages/auth/useRequestAuthentication';
import { useIsUserAuthenticatedQuery } from '@/packages/query-state/shared/store/monolith-python/cian-api/site/v1/get-user-data-by-request/query';
import { boundActionsFactory } from '@/packages/redux-state/store';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setActiveChatUrl: reduxActions.chat.setActiveChatUrl,
}));

type ChatButtonContainerProps = {
  chatUrl: string;
  render(props: { onClick(e: ReactMouseEvent): void }): JSX.Element;
};

const ChatButtonContainer: FC<ChatButtonContainerProps> = props => {
  const { render, chatUrl } = props;
  const ctx = useListingAppContext();
  const { data: isAuthenticated } = useIsUserAuthenticatedQuery();

  const requestAuthentication = useRequestAuthentication(ctx);

  const ca = useCAForCard();

  const boundActions = useBoundActions();

  const handleClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();

      ca({ action: 'send_message', category: 'Listing' }, { label: true });

      const onPopupOpen = (): void => {
        boundActions.setActiveChatUrl(chatUrl);

        ca({ action: 'open', category: 'Message_popup' }, { label: true });
      };

      if (isAuthenticated) {
        onPopupOpen();
      } else {
        requestAuthentication({
          onSuccess(): void {
            onPopupOpen();
          },
        });
      }
    },
    [boundActions, ca, chatUrl, isAuthenticated, requestAuthentication],
  );

  return render({
    onClick: handleClick,
  });
};

type ChatButtonRendererProps = Omit<ChatButtonContainerProps, 'chatUrl'>;

/**
 * @description Данный способ работы с классом нужен, что бы не вызывать множество внутренних хуков и не держать их в
 *   памяти, если у нас чатов нет вовсе
 */
export const ChatButtonRendererContainer: FC<ChatButtonRendererProps> = props => {
  const { chat } = useCardOfferDataContext();

  const chatUrl = chat?.url;

  if (!chatUrl) {
    return null;
  }

  return <ChatButtonContainer {...props} chatUrl={chatUrl} />;
};
